import Route from '@ember/routing/route';
import { getOwner } from '@ember/owner';
import type MetaService from 'madow-tech/services/meta';

export default class NotFoundRoute extends Route {
  model() {
    // Get intl service
    const owner = getOwner(this);
    if (!owner) return {};

    const intl = owner.lookup('service:intl');
    if (!intl) return {};

    // Get locale preference from localStorage (preferred) or cookie (fallback)
    const preferredLocale = this.getPreferredLocale();

    // Set locale based on user preference
    intl.setLocale([preferredLocale]);

    // Persist locale preference in localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem('locale', preferredLocale);
      } catch (e) {
        // localStorage might be disabled, fall back to cookie
        if (typeof document !== 'undefined') {
          document.cookie = `locale=${preferredLocale};path=/;max-age=31536000`;
        }
      }
    } else if (typeof document !== 'undefined') {
      // Fallback to cookie if localStorage not available
      document.cookie = `locale=${preferredLocale};path=/;max-age=31536000`;
    }

    // Set up SEO metadata for 404 page
    this.setupSEO(owner, preferredLocale);
    return { locale: preferredLocale };
  }

  private getPreferredLocale(): 'en' | 'es' {
    if (typeof window === 'undefined') return 'en';

    // Try localStorage first
    if (window.localStorage) {
      try {
        const stored = window.localStorage.getItem('locale');
        if (stored === 'en' || stored === 'es') {
          return stored;
        }
      } catch (e) {
        // localStorage might be disabled
      }
    }

    // Fallback to cookie
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'locale' && (value === 'en' || value === 'es')) {
          return value;
        }
      }
    }

    // Default to English
    return 'en';
  }

  private setupSEO(owner: ReturnType<typeof getOwner>, locale: string) {
    const meta = owner?.lookup('service:meta') as MetaService | undefined;
    if (!meta) return;

    // Reset previous meta
    meta.reset();

    // Set metadata for 404 page (localized) - noindex to prevent crawling
    if (locale === 'es') {
      meta.title = 'Página No Encontrada (404)';
      meta.description =
        'La página que buscas no se pudo encontrar. Regresa a la página de inicio de Madow Tech para explorar nuestros servicios de desarrollo de software.';
    } else {
      meta.title = 'Page Not Found (404)';
      meta.description =
        'The page you are looking for could not be found. Return to Madow Tech homepage to explore our software development services.';
    }
    meta.canonicalUrl = 'https://madow.tech/en';

    // Set hreflang alternate languages
    meta.alternateLanguages = [
      { lang: 'en', url: 'https://madow.tech/en' },
      { lang: 'es', url: 'https://madow.tech/es' },
    ];

    // Set custom meta tags - noindex to prevent 404 from being indexed
    meta.setTags([
      { name: 'robots', content: 'noindex, follow' },
      { property: 'og:type', content: 'website' },
      {
        property: 'og:locale',
        content: locale === 'es' ? 'es_AR' : 'en_US',
      },
      {
        property: 'og:locale:alternate',
        content: locale === 'es' ? 'en_US' : 'es_AR',
      },
      { name: 'language', content: locale === 'es' ? 'Spanish' : 'English' },
    ]);

    // Render meta to header
    meta.renderMetaToHeader();
  }
}
